import { useState } from "react";
import { ChatPanel, FloatingChatButton } from "@/features/assistant";
import { useBreadcrumb } from "@/platform/navigation";
import { ReportDetailsHeader } from "./components/report-details-header/report-details-header";
import { ReportStepsList } from "./components/report-steps-list";
import type { UiReportDetail, UiReportStepRow } from "../../queries/reports";

interface ReportDetailsPageProps {
  report: UiReportDetail | undefined;
  steps: UiReportStepRow[] | undefined;
  isLoading: boolean;
  editingId: string | null;
  draft: string;
  startingId: string | null;
  isRestarting: boolean;
  isExporting: boolean;
  isDeleting?: boolean;
  isReadOnly: boolean;
  onEdit: (step: UiReportStepRow) => void;
  onSave: (step: UiReportStepRow) => void;
  onCancel: () => void;
  onDraftChange: (draft: string) => void;
  onRestart: () => Promise<void>;
  onRestartStep: (stepId: string) => Promise<void>;
  onDelete: (step: UiReportStepRow) => void;
  onDeleteReport?: () => void;
  onUpdateMetadata: (values: {
    displayName: string;
    context: string;
  }) => Promise<void>;
  onExport: () => Promise<void>;
}

export function ReportDetailsPage({
  report,
  steps,
  isLoading,
  editingId,
  draft,
  startingId,
  isRestarting,
  isExporting,
  isDeleting = false,
  isReadOnly,
  onEdit,
  onSave,
  onCancel,
  onDraftChange,
  onRestart,
  onRestartStep,
  onDelete,
  onDeleteReport,
  onUpdateMetadata,
  onExport,
}: ReportDetailsPageProps) {
  useBreadcrumb(report?.displayName ?? null);

  const [isChatOpen, setIsChatOpen] = useState(false);
  const analysisId = report?.analysis_id;

  return (
    <>
      <title>{report?.displayName ?? "Report Details"} - Researchly</title>
      <div className="flex flex-col gap-6">
        <ReportDetailsHeader
          reportName={report?.displayName}
          reportStatus={report?.analysis_status}
          reportType={report?.analysis_type}
          displayDescription={report?.displayDescription}
          userContext={report?.userContext}
          displayInputResults={report?.displayInputResults}
          templateOrigin={report?.templateOrigin}
          createdAt={report?.record_created}
          updatedAt={report?.record_updated}
          isLoading={isLoading}
          isRestarting={isRestarting}
          isExporting={isExporting}
          isDeleting={isDeleting}
          isReadOnly={isReadOnly}
          onRestart={onRestart}
          onExport={onExport}
          onDeleteReport={onDeleteReport}
          onUpdateMetadata={onUpdateMetadata}
        />

        <ReportStepsList
          steps={steps}
          editingId={editingId}
          draft={draft}
          startingId={startingId}
          isReadOnly={isReadOnly}
          onEdit={onEdit}
          onSave={onSave}
          onCancel={onCancel}
          onDraftChange={onDraftChange}
          onStart={onRestartStep}
          onDelete={onDelete}
        />
      </div>

      {!isChatOpen && (
        <FloatingChatButton onClick={() => setIsChatOpen(true)} />
      )}
      <ChatPanel
        key={analysisId}
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        analysisId={analysisId ?? ""}
      />
    </>
  );
}
